import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { TtsVoice } from '../types/tts'
import { fetchVoices as apiFetchVoices, checkHealth } from '../services/ttsApi'

interface TtsContextValue {
  voices: TtsVoice[]
  voicesLoading: boolean
  voicesError: string | null
  ttsHealthy: boolean | null  // null = not checked yet
  loadVoices: (token: string) => Promise<void>
  refreshVoices: (token: string) => Promise<void>
  getVoiceById: (id: string) => TtsVoice | undefined
  checkTtsHealth: () => Promise<boolean>
}

const TtsContext = createContext<TtsContextValue | null>(null)

export const TtsProvider = ({ children }: { children: ReactNode }) => {
  const [voices, setVoices] = useState<TtsVoice[]>([])
  const [voicesLoading, setVoicesLoading] = useState(false)
  const [voicesError, setVoicesError] = useState<string | null>(null)
  // In dev bypass mode, pretend TTS is healthy so all tabs are accessible
  const devBypass = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BYPASS_AUTH === 'true'
  const [ttsHealthy, setTtsHealthy] = useState<boolean | null>(devBypass ? true : null)
  const [voicesLoaded, setVoicesLoaded] = useState(false)

  // Lazy health check - called by first TTS component to render, NOT on mount
  const checkTtsHealth = useCallback(async (): Promise<boolean> => {
    if (devBypass) { setTtsHealthy(true); return true }
    try {
      const healthy = await checkHealth()
      setTtsHealthy(healthy)
      return healthy
    } catch {
      setTtsHealthy(false)
      return false
    }
  }, [devBypass])

  // Load voices (only fetches once unless refreshVoices is called)
  const loadVoices = useCallback(async (token: string) => {
    if (voicesLoaded && voices.length > 0) return
    setVoicesLoading(true)
    setVoicesError(null)
    try {
      const fetchedVoices = await apiFetchVoices(token)
      setVoices(fetchedVoices)
      setVoicesLoaded(true)
    } catch (err: any) {
      setVoicesError(err.message || 'Failed to load voices')
    } finally {
      setVoicesLoading(false)
    }
  }, [voicesLoaded, voices.length])

  // Force refresh voices (after clone/delete)
  const refreshVoices = useCallback(async (token: string) => {
    setVoicesLoading(true)
    setVoicesError(null)
    try {
      const fetchedVoices = await apiFetchVoices(token)
      setVoices(fetchedVoices)
      setVoicesLoaded(true)
    } catch (err: any) {
      setVoicesError(err.message || 'Failed to refresh voices')
    } finally {
      setVoicesLoading(false)
    }
  }, [])

  const getVoiceById = useCallback(
    (id: string) => voices.find((v) => v.voice_id === id),
    [voices]
  )

  const value: TtsContextValue = {
    voices,
    voicesLoading,
    voicesError,
    ttsHealthy,
    loadVoices,
    refreshVoices,
    getVoiceById,
    checkTtsHealth,
  }

  return <TtsContext.Provider value={value}>{children}</TtsContext.Provider>
}

export const useTts = (): TtsContextValue => {
  const context = useContext(TtsContext)
  if (!context) {
    throw new Error('useTts must be used within a TtsProvider')
  }
  return context
}

export default TtsContext
